import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DocumentArrowDownIcon,
  PrinterIcon, // Add PrinterIcon
  CalendarDaysIcon,
  ClipboardDocumentCheckIcon,
  BanknotesIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface OrderStats {
  id: number;
  number: string;
  client: {
    id: number;
    name: string;
    director: string;
  };
  totalAmount: number;
  paidAmount: number;
  status: string;
  createdAt: string;
  structures: Array<{
    id: number;
    code: string;
    type: string;
    location: string;
    startDate: string;
    endDate: string;
    price: number;
    discount: number;
    total: number;
  }>;
}

const statusColors: Record<string, string> = {
  reserved: 'bg-yellow-100 text-yellow-800',
  waitingPayment: 'bg-blue-100 text-blue-800',
  partiallyPaid: 'bg-indigo-100 text-indigo-800',
  fullyPaid: 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800'
};

const statusLabels: Record<string, string> = {
  reserved: 'Бронь',
  waitingPayment: 'Ожидание оплаты',
  partiallyPaid: 'Частично оплачен',
  fullyPaid: 'Оплачен полностью',
  completed: 'Завершен',
  cancelled: 'Отменен'
};

const OrderReport: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orderStats, setOrderStats] = useState<OrderStats[]>([]);
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('orderDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Set default date range if not selected
        const end = new Date();
        const start = new Date();

        if (period === 'month') {
          start.setMonth(end.getMonth() - 1);
        } else if (period === 'quarter') {
          start.setMonth(end.getMonth() - 3);
        } else if (period === 'year') {
          start.setFullYear(end.getFullYear() - 1);
        }

        const formattedStartDate = startDate || start.toISOString().split('T')[0];
        const formattedEndDate = endDate || end.toISOString().split('T')[0];

        // Fetch order statistics
        const response = await axios.get('/reports/orders', {
          params: {
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            sortBy,
            sortOrder,
            status: statusFilter !== 'all' ? statusFilter : undefined
          }
        });

        setOrderStats(response.data.orders || []);

        // Update date inputs with actual dates used
        if (!startDate) setStartDate(formattedStartDate);
        if (!endDate) setEndDate(formattedEndDate);
      } catch (error) {
        console.error('Error fetching order report data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [period, sortBy, sortOrder, statusFilter]); // Added startDate and endDate dependencies

  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod);
    setStartDate('');
    setEndDate('');
  };

  const handleDateRangeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Update state, useEffect will trigger fetch
    if (startDate && endDate) {
      setPeriod('custom' + startDate + endDate);
    }
  };

  // Removed separate fetchOrderStats function

  const handleSortChange = (column: string) => {
    if (sortBy === column) {
      // Toggle sort order if the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort column and default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Open printable view in a new tab
  const handleOpenPrintView = () => {
    if (!startDate || !endDate) {
      alert('Пожалуйста, выберите или примените период для печати отчета.');
      return;
    }
    // Construct the URL for the print page with query parameters
    const params = new URLSearchParams({ startDate, endDate });
    if (statusFilter !== 'all') {
      params.append('status', statusFilter);
    }
    const printUrl = `/reports/orders/print?${params.toString()}`;
    // Open the URL in a new tab
    const printWindow = window.open(printUrl, '_blank');
    if (!printWindow) {
      alert('Не удалось открыть окно для печати. Пожалуйста, проверьте настройки блокировки всплывающих окон.');
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Calculate totals
  const totalRevenue = orderStats.reduce((sum, order) => sum + order.totalAmount, 0);
  const totalPaid = orderStats.reduce((sum, order) => sum + order.paidAmount, 0);
  const totalUnpaid = totalRevenue - totalPaid;

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Нет данных';
    return new Date(dateString).toLocaleDateString('ru-KZ');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <PageHeader
        title="Отчет по заказам"
        subtitle={`Статистика по заказам за период: ${startDate ? new Date(startDate).toLocaleDateString('ru-KZ') : '?'} - ${endDate ? new Date(endDate).toLocaleDateString('ru-KZ') : '?'}`}
        actions={
          <button
            onClick={handleOpenPrintView} // Use new handler
            className="btn-secondary flex items-center"
            disabled={!startDate || !endDate} // Disable if dates aren't set
          >
            <PrinterIcon className="h-5 w-5 mr-1" /> {/* Use PrinterIcon */}
            Печать
          </button>
        }
      />

      {/* Period selection */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 justify-between mb-4">
          <div className="flex space-x-2">
            <button
              className={`px-3 py-1 rounded ${period === 'month' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('month')}
            >
              Месяц
            </button>
            <button
              className={`px-3 py-1 rounded ${period === 'quarter' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('quarter')}
            >
              Квартал
            </button>
            <button
              className={`px-3 py-1 rounded ${period === 'year' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('year')}
            >
              Год
            </button>
          </div>

          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="all">Все статусы</option>
              <option value="reserved">Бронь</option>
              <option value="waitingPayment">Ожидание оплаты</option>
              <option value="partiallyPaid">Частично оплачен</option>
              <option value="fullyPaid">Оплачен полностью</option>
              <option value="completed">Завершен</option>
              <option value="cancelled">Отменен</option>
            </select>
          </div>
        </div>

        <form onSubmit={handleDateRangeSubmit} className="flex flex-col sm:flex-row gap-2 items-end">
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Начальная дата</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Конечная дата</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <button type="submit" className="btn-primary flex items-center">
            <CalendarDaysIcon className="h-5 w-5 mr-1" />
            Применить
          </button>
        </form>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Всего заказов</p>
              <p className="text-2xl font-bold text-gray-900">{orderStats.length}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <ClipboardDocumentCheckIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Общая сумма</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalRevenue)}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <BanknotesIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Оплачено</p>
              <p className="text-2xl font-bold text-green-700">{formatCurrency(totalPaid)}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <BanknotesIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Неоплачено</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalUnpaid)}</p>
            </div>
            <div className="bg-orange-100 p-3 rounded-full">
              <TagIcon className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Orders table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('number')}
                >
                  Номер заказа
                  {sortBy === 'number' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('client.name')}
                >
                  Клиент
                  {sortBy === 'client.name' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('status')}
                >
                  Статус
                  {sortBy === 'status' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('totalAmount')}
                >
                  Сумма
                  {sortBy === 'totalAmount' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('paidAmount')}
                >
                  Оплачено
                  {sortBy === 'paidAmount' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('createdAt')}
                >
                  Дата заказа
                  {sortBy === 'createdAt' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th>Конструкции</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orderStats.length > 0 ? (
                orderStats.map((order) => (
                  <tr
                    key={order.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/orders/${order.id}`)}
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {order.number}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {order.client.name}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                        {statusLabels[order.status] || order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {formatCurrency(order.totalAmount)}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2.5">
                          <div
                            className={`h-2.5 rounded-full ${order.paidAmount >= order.totalAmount ? 'bg-green-600' :
                                order.paidAmount > 0 ? 'bg-yellow-400' : 'bg-red-400'
                              }`}
                            style={{ width: `${(order.paidAmount / order.totalAmount) * 100}%` }}
                          ></div>
                        </div>
                        <span className="ml-2 text-sm text-gray-600">
                          {formatCurrency(order.paidAmount)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(order.createdAt)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {order.structures.map(s => s.code).join(', ')}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                    Нет данных для отображения
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrderReport;
